<script setup lang="ts">
import type { ApiKeyExpiry } from '@/types/apiKeys'

const MS_PER_DAY = 86_400_000

const props = defineProps<{
  open: boolean
  isSaving: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'create', payload: { name: string; expiry: ApiKeyExpiry }): void
}>()

const { t } = useI18n()
const { localDate } = useDate()

const name = ref('')
const expiry = ref<ApiKeyExpiry>('90')

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      name.value = ''
      expiry.value = '90'
    }
  }
)

const expiryOptions: { value: ApiKeyExpiry; label: string }[] = [
  { value: '30', label: t('pages.settings.apiKeys.create.expiryOptions.30') },
  { value: '90', label: t('pages.settings.apiKeys.create.expiryOptions.90') },
  { value: '180', label: t('pages.settings.apiKeys.create.expiryOptions.180') },
  { value: '365', label: t('pages.settings.apiKeys.create.expiryOptions.365') },
  { value: 'never', label: t('pages.settings.apiKeys.create.expiryOptions.never') }
]

const expiryDate = computed(() => {
  if (expiry.value === 'never') return null
  return new Date(Date.now() + Number(expiry.value) * MS_PER_DAY)
})

const canCreate = computed(() => name.value.trim().length > 0)

const submit = () => {
  if (!canCreate.value) return
  emit('create', { name: name.value.trim(), expiry: expiry.value })
}
</script>

<template>
  <BaseModal
    :is-open="open"
    :heading-title="t('pages.settings.apiKeys.create.title')"
    size="2xl"
    :cancel="t('pages.settings.apiKeys.create.cancel')"
    :link-text="t('pages.settings.apiKeys.create.submit')"
    :is-processing="isSaving"
    :is-disabled="!canCreate"
    @close="emit('update:open', false)"
    @click="submit"
  >
    <p class="mb-4 text-sm text-muted-foreground">{{ t('pages.settings.apiKeys.create.description') }}</p>

    <div class="flex flex-col gap-6">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-2">
          <Label for="api-key-name">{{ t('pages.settings.apiKeys.create.nameLabel') }}</Label>
          <Input
            id="api-key-name"
            v-model="name"
            maxlength="50"
            :placeholder="t('pages.settings.apiKeys.create.namePlaceholder')"
          />
          <span class="text-xs text-muted-foreground">{{ t('pages.settings.apiKeys.create.nameHelp') }}</span>
        </div>

        <div class="flex flex-col gap-2">
          <Label>{{ t('pages.settings.apiKeys.create.expiryLabel') }}</Label>
          <Select v-model="expiry">
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="option in expiryOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </SelectItem>
            </SelectContent>
          </Select>
          <span v-if="expiry === 'never'" class="text-xs text-amber-600">
            {{ t('pages.settings.apiKeys.create.expiryNeverWarning') }}
          </span>
          <span v-else class="text-xs text-muted-foreground">
            {{ t('pages.settings.apiKeys.create.expiryHint', { date: expiryDate ? localDate(expiryDate) : '' }) }}
          </span>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
